import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem,
} from '@mui/material'
import {
  createActionRoutine,
  selectMutationLoading,
} from 'features/actions/ducks'
import HintBadge from 'features/compliance/HintBadge'
import { promisifyRoutine } from 'redux-saga-routines'
import SubmitError from './SubmitError'
import { firstErrorMessage } from 'features/actions/ducks/messages'

const createAsync = promisifyRoutine(createActionRoutine)

const SEVERITIES = [
  { value: 'critical', label: 'Critical' },
  { value: 'high',     label: 'High' },
  { value: 'medium',   label: 'Medium' },
  { value: 'low',      label: 'Low' },
]

const dialogPaperSx = {
  borderRadius: '12px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
}

const dialogTitleSx = {
  fontSize: '18px',
  fontWeight: 600,
  color: '#0C1229',
  padding: '20px 24px',
  borderBottom: '1px solid #f2f2f2',
}

const dialogContentSx = {
  padding: '20px 24px !important',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
}

const dialogActionsSx = {
  padding: '16px 24px',
  borderTop: '1px solid #f2f2f2',
  gap: '8px',
}

const cancelButtonSx = {
  textTransform: 'none',
  fontWeight: 500,
  color: '#0C1229',
  borderColor: '#f2f2f2',
  borderRadius: '8px',
  padding: '8px 20px',
  '&:hover': {
    backgroundColor: '#f2f2f2',
    borderColor: '#0C1229',
  },
}

const submitButtonSx = {
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '15px',
  minHeight: '40px',
  backgroundColor: '#33becc',
  color: '#0C1229',
  borderRadius: '8px',
  padding: '8px 24px',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#2aa6b3',
    boxShadow: 'none',
  },
}

/**
 * AddActionDialog
 *
 * Props:
 *   - buildingPassportId: when set, the action is locked to this BP (no picker
 *     shown). This is the "BP details" / "single building" case.
 *   - availableBps: array of { id, name } for the BP picker. Used on portfolio
 *     and global views where the user has to choose where the action lives.
 */
const AddActionDialog = ({ open, onClose, buildingPassportId, availableBps }) => {
  const dispatch = useDispatch()
  const submitting = useSelector(selectMutationLoading)
  const [submitError, setSubmitError] = useState(null)
  useEffect(() => { if (open) setSubmitError(null) }, [open])
  const [title, setTitle] = useState('')
  const [severity, setSeverity] = useState('medium')
  const [description, setDescription] = useState('')
  const [dueBy, setDueBy] = useState('')
  const [bpId, setBpId] = useState(buildingPassportId || '')
  const [obligationId, setObligationId] = useState(null)

  const showBpPicker = !buildingPassportId
  const bpOptions = Array.isArray(availableBps) ? availableBps : []

  // When the dialog re-opens, prefill from props and pre-select the only BP
  // option if there's only one — saves the user a click.
  useEffect(() => {
    if (!open) return
    setTitle('')
    setSeverity('medium')
    setDescription('')
    setDueBy('')
    setObligationId(null)
    if (buildingPassportId) {
      setBpId(buildingPassportId)
    } else if (bpOptions.length === 1) {
      setBpId(bpOptions[0].id)
    } else {
      setBpId('')
    }
    // eslint-disable-next-line
  }, [open, buildingPassportId])

  const submit = async () => {
    setSubmitError(null)
    try {
      await createAsync({
        buildingPassportId: buildingPassportId || bpId,
        title,
        description: description || null,
        severity,
        suggestedDueBy: dueBy || null,
        ...(obligationId ? { programmeObligationId: obligationId } : {}),
      }, dispatch)
      onClose?.()
    } catch (e) {
      setSubmitError(firstErrorMessage(e, 'Your remedial was not saved. Check the details and try again.'))
    }
  }

  const applyHint = hint => {
    const payload = hint?.payload || {}
    if (payload.severity) setSeverity(payload.severity)
    if (payload.programmeObligationId) setObligationId(payload.programmeObligationId)
  }

  const canSubmit = !!title.trim() && (!!buildingPassportId || !!bpId) && !submitting

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: dialogPaperSx }}
    >
      <DialogTitle sx={dialogTitleSx}>Add remedial</DialogTitle>
      <DialogContent sx={dialogContentSx}>
        {showBpPicker && (
          <TextField
            label="Building Passport"
            select
            required
            fullWidth
            size="small"
            value={bpId}
            onChange={e => setBpId(e.target.value)}
            helperText={
              bpOptions.length === 0
                ? 'None of your Building Passports have task management switched on yet. A remedial needs a Building Passport with task management.'
                : 'Pick which Building Passport this remedial belongs to'
            }
          >
            {bpOptions.map(bp => (
              <MenuItem key={bp.id} value={bp.id}>{bp.name}</MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          label="Title"
          required
          fullWidth
          size="small"
          value={title}
          onChange={e => setTitle(e.target.value)}
        />
        <TextField
          label="Severity"
          select
          required
          fullWidth
          size="small"
          value={severity}
          onChange={e => setSeverity(e.target.value)}
        >
          {SEVERITIES.map(s => (
            <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
          ))}
        </TextField>
        <TextField
          label="Description"
          multiline
          rows={3}
          fullWidth
          size="small"
          value={description}
          onChange={e => setDescription(e.target.value)}
        />
        <TextField
          label="Due date (optional)"
          type="date"
          fullWidth
          size="small"
          InputLabelProps={{ shrink: true }}
          value={dueBy}
          onChange={e => setDueBy(e.target.value)}
          helperText="Leave blank and we set one from the severity: critical 7 days, high 30, medium 90, low 180. You can change it later."
        />
        <HintBadge
          context="action_create"
          buildingPassportId={buildingPassportId || bpId || undefined}
          payload={{ title, description }}
          onApply={applyHint}
        />
      </DialogContent>
      {submitError && <SubmitError role='alert'>{submitError}</SubmitError>}
      <DialogActions sx={dialogActionsSx}>
        <Button onClick={onClose} variant="outlined" sx={cancelButtonSx}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!canSubmit}
          sx={submitButtonSx}
        >
          Add remedial
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AddActionDialog
